/**
 * Zalo Channel Management Page
 * Quản lý danh sách tài khoản Zalo đã kết nối
 */
import React, { Component } from 'react';
import { Modal, Button, Spin, Tooltip, notification, Switch, Pagination, Empty, Input, Table, Avatar, Tag } from 'antd';
import { fetchData } from '../../../lib/apis';
import { MobileOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, SyncOutlined, TeamOutlined, LoginOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import moment from 'moment';
import ZaloConnect from './ZaloConnect';

export default class ZaloChannel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listData: [],
            total: 0,
            loading: false,
            visibleZaloConnect: false,
            reconnectChannel: null,
            // Friends modal state
            friendsModalVisible: false,
            selectedChannel: null,
            friends: [],
            friendsTotal: 0,
            friendsLoading: false,
            friendsSearch: '',
            friendsPage: 1,
            friendsLimit: 20,
            // Sync states
            syncingChannels: {},
            checkingChannels: {},
            friendsCounts: {}
        };
        this.limit = 20;
        this.page = 1;
    }

    componentDidMount() {
        this.getListData();
    }

    getListData = async () => {
        try {
            this.setState({ loading: true });
            const params = new URLSearchParams({
                channel_type: 'zalo',
                limit: this.limit,
                page: this.page
            });

            let res = await fetchData({
                url: `api/v1/channel/list?${params.toString()}`
            });

            this.setState({
                listData: res.data || [],
                total: res.total || 0,
                loading: false
            });

            // Load friends count for each channel
            (res.data || []).forEach(channel => {
                this.loadFriendsCount(channel._id);
            });
        } catch (err) {
            this.setState({ loading: false });
            console.error('Error fetching Zalo channels:', err);
        }
    }

    loadFriendsCount = async (channelId) => {
        try {
            const res = await fetchData({
                url: `api/v1/channel/zalo/${channelId}/friends/count`
            });
            this.setState(prev => ({
                friendsCounts: { ...prev.friendsCounts, [channelId]: res.count || 0 }
            }));
        } catch (err) {
            console.error('Error loading friends count:', err);
        }
    }

    deleteChannel = async (id) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Tài khoản sẽ được gỡ khỏi công ty và các hội thoại Zalo liên quan sẽ tạm ẩn. Lịch sử sẽ hiện lại khi kết nối lại đúng tài khoản Zalo này.',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    await fetchData({
                        url: `api/v1/channel/delete/`,
                        method: 'delete',
                        data: { ids: [id] }
                    });
                    notification.success({
                        message: 'Thành công',
                        description: 'Đã xóa tài khoản Zalo'
                    });
                    this.getListData();
                } catch (err) {
                    notification.error({
                        message: 'Lỗi',
                        description: err.message || 'Không thể xóa tài khoản'
                    });
                }
            }
        });
    }

    enableChannel = async (id, enable) => {
        try {
            await fetchData({
                url: `api/v1/channel/enable`,
                method: 'post',
                data: { enable: enable ? 1 : 0, ids: [id] }
            });
            notification.success({
                message: 'Thành công',
                description: enable ? 'Đã bật kích hoạt' : 'Đã tắt kích hoạt'
            });
        } catch (err) {
            notification.error({
                message: 'Lỗi',
                description: err.message || 'Thao tác thất bại'
            });
        }
    }

    syncFriends = async (channelId) => {
        this.setState(prev => ({
            syncingChannels: { ...prev.syncingChannels, [channelId]: true }
        }));

        try {
            await fetchData({
                url: `api/v1/channel/zalo/${channelId}/sync-friends`,
                method: 'post'
            });
            notification.success({
                message: 'Thành công',
                description: 'Đang đồng bộ danh bạ, vui lòng chờ...'
            });
            // Reload count after 3 seconds
            setTimeout(() => {
                this.loadFriendsCount(channelId);
            }, 3000);
        } catch (err) {
            notification.error({
                message: 'Lỗi',
                description: err.message || 'Không thể đồng bộ danh bạ'
            });
        } finally {
            this.setState(prev => ({
                syncingChannels: { ...prev.syncingChannels, [channelId]: false }
            }));
        }
    }

    checkSession = async (channelId) => {
        this.setState(prev => ({
            checkingChannels: { ...prev.checkingChannels, [channelId]: true }
        }));
        try {
            const response = await fetchData({
                url: `api/v1/channel/zalo/${channelId}/check-session`,
                method: 'post',
                data: {}
            });
            const status = response.data && response.data.status;
            if (status === 'active') {
                notification.success({
                    message: 'Phiên Zalo hợp lệ',
                    description: response.data.message
                });
            } else {
                notification.warning({
                    message: status === 'expired' ? 'Phiên Zalo đã hết hạn' : 'Không thể kiểm tra phiên',
                    description: response.data.error || response.data.message
                });
            }
            await this.getListData();
        } catch (err) {
            notification.error({
                message: 'Kiểm tra thất bại',
                description: err.message || 'Không thể kiểm tra phiên Zalo'
            });
        } finally {
            this.setState(prev => ({
                checkingChannels: { ...prev.checkingChannels, [channelId]: false }
            }));
        }
    }

    openFriendsModal = async (channel) => {
        this.setState({
            friendsModalVisible: true,
            selectedChannel: channel,
            friendsSearch: '',
            friendsPage: 1
        }, () => {
            this.loadFriends();
        });
    }

    loadFriends = async () => {
        const { selectedChannel, friendsSearch, friendsPage, friendsLimit } = this.state;
        if (!selectedChannel) return;

        this.setState({ friendsLoading: true });

        try {
            const params = new URLSearchParams({
                page: friendsPage,
                limit: friendsLimit,
                search: friendsSearch
            });
            const res = await fetchData({
                url: `api/v1/channel/zalo/${selectedChannel._id}/friends?${params.toString()}`
            });
            this.setState({
                friends: res.data || [],
                friendsTotal: res.total || 0,
                friendsLoading: false
            });
        } catch (err) {
            this.setState({ friendsLoading: false });
            console.error('Error loading friends:', err);
        }
    }

    render() {
        const { friendsModalVisible, friends, friendsTotal, friendsLoading, friendsSearch, friendsPage, friendsLimit, selectedChannel, syncingChannels, checkingChannels, friendsCounts } = this.state;

        const friendsColumns = [
            {
                title: 'Avatar',
                dataIndex: 'avatar',
                width: 60,
                render: (avatar) => <Avatar src={avatar} icon={<MobileOutlined />} />
            },
            {
                title: 'Tên',
                dataIndex: 'display_name',
                ellipsis: true
            },
            {
                title: 'SĐT',
                dataIndex: 'phone',
                width: 120,
                render: (phone) => phone || '-'
            },
            {
                title: 'Zalo ID',
                dataIndex: 'user_id',
                width: 150,
                ellipsis: true
            }
        ];

        return (
            <div className="list-connect content-m">
                <Modal
                    title={null}
                    visible={this.state.visibleZaloConnect}
                    onCancel={() => this.setState({ visibleZaloConnect: false, reconnectChannel: null })}
                    footer={null}
                    width={720}
                    className="zalo-connect-modal"
                    centered
                    destroyOnClose={true}
                >
                    <ZaloConnect 
                        companyId={this.props.UserLogin?.company_id || this.props.companySelected?.company_id}
                        reconnectChannelId={this.state.reconnectChannel && this.state.reconnectChannel._id}
                        reconnectChannelName={this.state.reconnectChannel && this.state.reconnectChannel.full_name}
                        onSuccess={() => {
                            this.setState({ visibleZaloConnect: false, reconnectChannel: null });
                            this.getListData();
                        }}
                        onCancel={() => this.setState({ visibleZaloConnect: false })}
                    />
                </Modal>

                {/* Friends Modal */}
                <Modal
                    title={<><TeamOutlined /> Danh bạ Zalo - {selectedChannel?.full_name}</>}
                    visible={friendsModalVisible}
                    onCancel={() => this.setState({ friendsModalVisible: false })}
                    footer={null}
                    width={700}
                    destroyOnClose={true}
                >
                    <div style={{ marginBottom: 16 }}>
                        <Input.Search
                            placeholder="Tìm kiếm theo tên..."
                            value={friendsSearch}
                            onChange={(e) => this.setState({ friendsSearch: e.target.value })}
                            onSearch={() => this.setState({ friendsPage: 1 }, this.loadFriends)}
                            style={{ width: 300 }}
                            allowClear
                        />
                        <span style={{ marginLeft: 16, color: '#666' }}>
                            Tổng: {friendsTotal} bạn bè
                        </span>
                    </div>
                    <Table
                        dataSource={friends}
                        columns={friendsColumns}
                        rowKey="_id"
                        loading={friendsLoading}
                        pagination={{
                            current: friendsPage,
                            pageSize: friendsLimit,
                            total: friendsTotal,
                            onChange: (page) => this.setState({ friendsPage: page }, this.loadFriends)
                        }}
                        size="small"
                    />
                </Modal>

                <div className="list-c">
                    <div style={{ marginBottom: "15px" }} className="table-header">
                        <h3>
                            <MobileOutlined style={{ marginRight: 8, color: '#0068FF' }} />
                            Quản lý tài khoản Zalo: {this.state.total}
                        </h3>
                        <div className="d-flex overflow-auto" style={{ marginBottom: "15px" }}>
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                                style={{ background: '#0068FF', borderColor: '#0068FF', marginRight: 8 }}
                                onClick={() => this.setState({ visibleZaloConnect: true, reconnectChannel: null })}
                            >
                                Thêm tài khoản Zalo
                            </Button>
                            <Tooltip title="Làm mới">
                                <Button icon={<ReloadOutlined />} onClick={this.getListData} />
                            </Tooltip>
                        </div>
                        <div className="d-flex justify-content-end">
                            <Pagination
                                total={this.state.total}
                                showSizeChanger
                                pageSize={this.limit}
                                pageSizeOptions={[20, 50, 100]}
                                current={this.page}
                                onShowSizeChange={(current, size) => {
                                    this.limit = size;
                                    this.getListData();
                                }}
                                onChange={(current) => {
                                    this.page = current;
                                    this.getListData();
                                }}
                            />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Spin spinning={this.state.loading}>
                            {this.state.listData.length > 0 ? (
                                <table className="table table-striped table-vcenter">
                                    <thead>
                                        <tr>
                                            <th>STT</th>
                                            <th>Ảnh</th>
                                            <th>Zalo ID</th>
                                            <th>Tên</th>
                                            <th>Danh bạ</th>
                                            <th>Trạng thái</th>
                                            <th>Kích hoạt</th>
                                            <th>Thời gian kết nối</th>
                                            <th className="text-right">Hành động</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.listData.map((item, i) => {
                                            const stt = this.limit * (this.page - 1) + (i + 1);
                                            const friendCount = friendsCounts[item._id] || 0;
                                            const isSyncing = syncingChannels[item._id];
                                            const isChecking = checkingChannels[item._id];
                                            const sessionStatus = (item.zalo_credentials && item.zalo_credentials.session_status) || 'unknown';
                                            const sessionMeta = {
                                                active: { color: 'green', text: 'Phiên hợp lệ' },
                                                expired: { color: 'red', text: 'Hết hạn' },
                                                error: { color: 'orange', text: 'Lỗi kiểm tra' },
                                                unknown: { color: 'blue', text: 'Chưa kiểm tra' }
                                            }[sessionStatus] || { color: 'blue', text: 'Chưa kiểm tra' };
                                            const sessionUnavailable = ['expired', 'error'].includes(sessionStatus);
                                            return (
                                                <tr key={item._id}>
                                                    <td>{stt}</td>
                                                    <td>
                                                        <div style={{ width: "35px", height: "35px", position: "relative" }}>
                                                            {item.avatar ? (
                                                                <img className="avatar" src={item.avatar} alt="" />
                                                            ) : (
                                                                <div style={{
                                                                    width: 35,
                                                                    height: 35,
                                                                    borderRadius: '50%',
                                                                    background: '#0068FF',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: '#fff',
                                                                    fontSize: 16
                                                                }}>
                                                                    <MobileOutlined />
                                                                </div>
                                                            )}
                                                            <span 
                                                                style={{ position: "absolute", bottom: "0px", right: "0px" }} 
                                                                className={`badge-status badge badge-${sessionStatus === 'active' ? 'success' : sessionStatus === 'expired' ? 'danger' : 'warning'}`}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>{item.user_id}</td>
                                                    <td>{item.full_name}</td>
                                                    <td>
                                                        <Button 
                                                            type="link" 
                                                            onClick={() => this.openFriendsModal(item)}
                                                            style={{ padding: 0 }}
                                                        >
                                                            <TeamOutlined /> {friendCount} bạn
                                                        </Button>
                                                    </td>
                                                    <td>
                                                        <Tooltip title={(item.zalo_credentials && item.zalo_credentials.last_error) || ''}>
                                                            <Tag color={sessionMeta.color}>{sessionMeta.text}</Tag>
                                                        </Tooltip>
                                                    </td>
                                                    <td>
                                                        <Switch 
                                                            defaultChecked={item.enable === 1}
                                                            onChange={(e) => this.enableChannel(item._id, e)}
                                                        />
                                                    </td>
                                                    <td>{moment(item.created_time).format('HH:mm DD/MM/YYYY')}</td>
                                                    <td className="text-right">
                                                        <Tooltip title="Đồng bộ danh bạ">
                                                            <Button 
                                                                size="small" 
                                                                icon={<SyncOutlined spin={isSyncing} />}
                                                                onClick={() => this.syncFriends(item._id)}
                                                                loading={isSyncing}
                                                                disabled={sessionUnavailable}
                                                                style={{ marginRight: 4 }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Kiểm tra phiên Zalo">
                                                            <Button
                                                                size="small"
                                                                icon={<SafetyCertificateOutlined />}
                                                                onClick={() => this.checkSession(item._id)}
                                                                loading={isChecking}
                                                                style={{ marginRight: 4 }}
                                                            />
                                                        </Tooltip>
                                                        {sessionUnavailable && (
                                                            <Tooltip title="Quét QR để kết nối lại">
                                                                <Button
                                                                    size="small"
                                                                    type="primary"
                                                                    icon={<LoginOutlined />}
                                                                    onClick={() => this.setState({ visibleZaloConnect: true, reconnectChannel: item })}
                                                                    style={{ marginRight: 4, background: '#0068FF', borderColor: '#0068FF' }}
                                                                >
                                                                    Kết nối lại
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Xóa">
                                                            <Button 
                                                                size="small" 
                                                                type="danger" 
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => this.deleteChannel(item._id)}
                                                            />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <Empty 
                                    description="Chưa có tài khoản Zalo nào được kết nối"
                                    style={{ padding: 40 }}
                                >
                                    <Button 
                                        type="primary" 
                                        icon={<PlusOutlined />}
                                        style={{ background: '#0068FF', borderColor: '#0068FF' }}
                                        onClick={() => this.setState({ visibleZaloConnect: true, reconnectChannel: null })}
                                    >
                                        Thêm tài khoản Zalo
                                    </Button>
                                </Empty>
                            )}
                        </Spin>
                    </div>
                </div>
            </div>
        );
    }
}
